import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Bell,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
  Check,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  FileText,
} from 'lucide-react';

import EmployeeSidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Navbar';
import api from '../../services/api_client';

import './Alertes.css';

/* =========================================================
   CONFIGURATION
========================================================= */

const TYPE_LABELS = {
  CONTRACT_EXPIRING: 'Contrat bientôt expiré',
  CONTRACT_EXPIRED: 'Contrat expiré',
  PROBATION_ENDING: "Fin de période d'essai",
  DRAFT_CONTRACT: 'Contrat en brouillon',
  OTHER_RH_ALERT: 'Alerte RH',
};

const PRIORITE_CONFIG = {
  CRITICAL: {
    label: 'Critique',
    icon: AlertTriangle,
    cls: 'critical',
  },

  WARNING: {
    label: 'Avertissement',
    icon: AlertCircle,
    cls: 'warning',
  },

  INFO: {
    label: 'Information',
    icon: Info,
    cls: 'info',
  },
};

/* =========================================================
   HELPERS
========================================================= */

function formatDateHeure(iso) {
  if (!iso) {
    return '-';
  }

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return '-';
    }

    const date = d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const heure = d.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${date} à ${heure}`;
  } catch {
    return '-';
  }
}

/**
 * Transforme différentes réponses possibles de l'API
 * en tableau.
 *
 * Formats acceptés :
 *
 * [
 *   {...},
 *   {...}
 * ]
 *
 * ou
 *
 * {
 *   items: [...]
 * }
 *
 * ou
 *
 * {
 *   notifications: [...]
 * }
 *
 * ou
 *
 * {
 *   data: [...]
 * }
 *
 * ou
 *
 * {
 *   data: {
 *      items: [...]
 *   }
 * }
 */
function normalizeAlertesResponse(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  if (Array.isArray(data.items)) {
    return data.items;
  }

  if (Array.isArray(data.notifications)) {
    return data.notifications;
  }

  if (Array.isArray(data.data)) {
    return data.data;
  }

  if (
    data.data &&
    typeof data.data === 'object' &&
    Array.isArray(data.data.items)
  ) {
    return data.data.items;
  }

  if (
    data.data &&
    typeof data.data === 'object' &&
    Array.isArray(data.data.notifications)
  ) {
    return data.data.notifications;
  }

  return [];
}

/**
 * Retourne un nombre valide.
 */
function safeNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
}

/* =========================================================
   BADGE PRIORITE
========================================================= */

function PrioriteBadge({ priorite }) {
  const config =
    PRIORITE_CONFIG[priorite] || {
      label: priorite || 'Information',
      icon: Info,
      cls: 'info',
    };

  const Icon = config.icon;

  return (
    <span
      className={`alerte-priorite-badge alerte-${config.cls}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function EmployeeAlertes() {
  const navigate = useNavigate();

  /* =======================================================
     STATE
  ======================================================= */

  const [alertes, setAlertes] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [totalGlobal, setTotalGlobal] = useState(0);

  const [unreadCount, setUnreadCount] = useState(0);

  /* Filtres */

  const [filterLue, setFilterLue] = useState('');

  const [filterPriorite, setFilterPriorite] = useState('');

  /* Pagination */

  const [page, setPage] = useState(1);

  const pageSize = 10;

  const [totalPages, setTotalPages] = useState(1);

  /* =======================================================
     FETCH ALERTES
  ======================================================= */

  const fetchAlertes = useCallback(async () => {
    setLoading(true);

    setError('');

    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (filterLue !== '') {
        params.est_lue = filterLue;
      }

      if (filterPriorite) {
        params.priorite = filterPriorite;
      }

      console.log(
        '[ALERTES] Récupération des notifications...',
        params
      );

      const res = await api.get('/notifications/', {
        params,
      });

      console.log(
        '[ALERTES] Réponse API:',
        res.data
      );

      /* ===================================================
         NORMALISATION
      =================================================== */

      const safeItems = normalizeAlertesResponse(res.data);

      /*
       * IMPORTANT :
       * On garantit toujours un tableau.
       */
      setAlertes(
        Array.isArray(safeItems)
          ? safeItems
          : []
      );

      /* ===================================================
         STATISTIQUES
      =================================================== */

      const total =
        safeNumber(
          res.data?.total,
          safeItems.length
        );

      const unread =
        safeNumber(
          res.data?.unread_count,
          safeItems.filter(
            (alerte) =>
              alerte &&
              alerte.est_lue === false
          ).length
        );

      const calculatedTotalPages =
        safeNumber(
          res.data?.total_pages,
          Math.max(
            1,
            Math.ceil(total / pageSize)
          )
        );

      setTotalGlobal(total);

      setUnreadCount(unread);

      setTotalPages(
        Math.max(
          1,
          calculatedTotalPages
        )
      );

      console.log(
        '[ALERTES] Items:',
        safeItems
      );

      console.log(
        '[ALERTES] Total:',
        total
      );

      console.log(
        '[ALERTES] Non lues:',
        unread
      );
    } catch (err) {
      console.error(
        '[ALERTES] Erreur:',
        err
      );

      /*
       * IMPORTANT :
       * Même en cas d'erreur,
       * on remet alertes à [].
       */
      setAlertes([]);

      setTotalGlobal(0);

      setUnreadCount(0);

      setTotalPages(1);

      setError(
        'Impossible de récupérer vos alertes.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    page,
    filterLue,
    filterPriorite,
  ]);

  /* =======================================================
     EFFECTS
  ======================================================= */

  useEffect(() => {
    fetchAlertes();
  }, [fetchAlertes]);

  useEffect(() => {
    setPage(1);
  }, [
    filterLue,
    filterPriorite,
  ]);

  /* =======================================================
     SAFE ARRAY
  ======================================================= */

  /*
   * On ne travaille JAMAIS directement avec alertes
   * pour les opérations .filter() / .map().
   *
   * On utilise toujours safeAlertes.
   */

  const safeAlertes = Array.isArray(alertes)
    ? alertes
    : [];

  /* =======================================================
     STATISTIQUES PAGE
  ======================================================= */

  const totalCritiques =
    safeAlertes.filter(
      (alerte) =>
        alerte &&
        alerte.priorite === 'CRITICAL'
    ).length;

  const totalWarning =
    safeAlertes.filter(
      (alerte) =>
        alerte &&
        alerte.priorite === 'WARNING'
    ).length;

  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const handleReset = () => {
    setFilterLue('');

    setFilterPriorite('');

    setPage(1);
  };

  const hasActiveFilters =
    filterLue !== '' ||
    filterPriorite !== '';

  /* =======================================================
     MARQUER UNE ALERTE COMME LUE
  ======================================================= */

  const handleMarkRead = async (id) => {
    if (!id) {
      return;
    }

    try {
      await api.post(
        `/notifications/${id}/read`
      );

      await fetchAlertes();
    } catch (err) {
      console.error(
        '[ALERTES] Erreur marquage:',
        err
      );

      alert(
        'Erreur lors du marquage comme lue.'
      );
    }
  };

  /* =======================================================
     MARQUER TOUTES LES ALERTES COMME LUES
  ======================================================= */

  const handleMarkAllRead = async () => {
    try {
      await api.post(
        '/notifications/mark-all-read'
      );

      await fetchAlertes();
    } catch (err) {
      console.error(
        '[ALERTES] Erreur marquage global:',
        err
      );

      alert(
        'Erreur lors du marquage global.'
      );
    }
  };

  /* =======================================================
     VOIR CONTRAT
  ======================================================= */

  const handleVoirContrat = async (alerte) => {
    if (!alerte) {
      return;
    }

    if (
      !alerte.est_lue &&
      alerte.id
    ) {
      try {
        await api.post(
          `/notifications/${alerte.id}/read`
        );
      } catch (err) {
        console.warn(
          '[ALERTES] Impossible de marquer comme lue:',
          err
        );
      }
    }

    navigate(
      '/mon-espace/contrats'
    );
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const renderPageNumbers = () => {
    const pages = [];

    const safeTotalPages = Math.max(
      1,
      Number(totalPages) || 1
    );

    const safePage = Math.min(
      Math.max(
        1,
        Number(page) || 1
      ),
      safeTotalPages
    );

    if (safeTotalPages <= 5) {
      for (
        let i = 1;
        i <= safeTotalPages;
        i++
      ) {
        pages.push(i);
      }
    } else if (safePage <= 3) {
      pages.push(
        1,
        2,
        3,
        4,
        '...',
        safeTotalPages
      );
    } else if (
      safePage >=
      safeTotalPages - 2
    ) {
      pages.push(
        1,
        '...',
        safeTotalPages - 3,
        safeTotalPages - 2,
        safeTotalPages - 1,
        safeTotalPages
      );
    } else {
      pages.push(
        1,
        '...',
        safePage - 1,
        safePage,
        safePage + 1,
        '...',
        safeTotalPages
      );
    }

    return pages.map((p, index) => {
      if (p === '...') {
        return (
          <span
            key={`ellipsis-${index}`}
            className="pagination-ellipsis"
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={`page-${p}`}
          onClick={() => setPage(p)}
          className={`pagination-circle-btn ${
            safePage === p
              ? 'active'
              : ''
          }`}
        >
          {p}
        </button>
      );
    });
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="app-container">

      {/* SIDEBAR */}


      {/* MAIN */}

      <main className="main-content">


        <div className="content-padding">

          {/* =================================================
              STATISTIQUES
          ================================================= */}

          <div className="stats-grid alertes-stats">

            {/* TOTAL */}

            <div className="stat-card glass-card">

              <div className="stat-icon-wrapper purple">
                <Bell
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">

                <span className="stat-label">
                  Total alertes
                </span>

                <span className="stat-value">
                  {totalGlobal}
                </span>

                <span className="stat-subtitle">
                  Alertes personnelles
                </span>

              </div>
            </div>

            {/* NON LUES */}

            <div className="stat-card glass-card">

              <div className="stat-icon-wrapper blue">
                <Info
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">

                <span className="stat-label">
                  Non lues
                </span>

                <span className="stat-value">
                  {unreadCount}
                </span>

                <span className="stat-subtitle">
                  Nécessitent votre attention
                </span>

              </div>
            </div>

            {/* WARNING */}

            <div className="stat-card glass-card">

              <div className="stat-icon-wrapper orange">
                <AlertCircle
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">

                <span className="stat-label">
                  Avertissements (page)
                </span>

                <span className="stat-value">
                  {totalWarning}
                </span>

                <span className="stat-subtitle">
                  Priorité WARNING
                </span>

              </div>
            </div>

            {/* CRITICAL */}

            <div className="stat-card glass-card">

              <div className="stat-icon-wrapper red">
                <AlertTriangle
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">

                <span className="stat-label">
                  Critiques (page)
                </span>

                <span className="stat-value">
                  {totalCritiques}
                </span>

                <span className="stat-subtitle">
                  Échéance imminente ou dépassée
                </span>

              </div>
            </div>

          </div>

          {/* =================================================
              LISTE
          ================================================= */}

          <div className="table-card glass-card">

            {/* FILTRES */}

            <div className="alertes-filters-bar">

              {/* STATUT */}

              <div className="alertes-filter-group">

                <span className="alertes-filter-label">
                  Statut
                </span>

                <select
                  value={filterLue}
                  onChange={(e) =>
                    setFilterLue(
                      e.target.value
                    )
                  }
                  className="alertes-filter-select"
                >

                  <option value="">
                    Toutes
                  </option>

                  <option value="false">
                    Non lues
                  </option>

                  <option value="true">
                    Lues
                  </option>

                </select>

              </div>

              {/* PRIORITE */}

              <div className="alertes-filter-group">

                <span className="alertes-filter-label">
                  Priorité
                </span>

                <select
                  value={filterPriorite}
                  onChange={(e) =>
                    setFilterPriorite(
                      e.target.value
                    )
                  }
                  className="alertes-filter-select"
                >

                  <option value="">
                    Toutes
                  </option>

                  <option value="CRITICAL">
                    Critique
                  </option>

                  <option value="WARNING">
                    Avertissement
                  </option>

                  <option value="INFO">
                    Information
                  </option>

                </select>

              </div>

              {/* ACTIONS */}

              <div className="alertes-filter-actions">

                {hasActiveFilters && (
                  <button
                    className="alertes-reset-btn"
                    onClick={handleReset}
                  >
                    <RotateCcw size={14} />

                    Réinitialiser
                  </button>
                )}

                {unreadCount > 0 && (
                  <button
                    className="alertes-markall-btn"
                    onClick={
                      handleMarkAllRead
                    }
                  >
                    <CheckCheck size={14} />

                    Tout marquer comme lu
                  </button>
                )}

              </div>

            </div>

            {/* =================================================
                CONTENU
            ================================================= */}

            {loading ? (

              <div className="table-message">
                Chargement de vos alertes...
              </div>

            ) : error ? (

              <div className="table-message error">
                {error}
              </div>

            ) : safeAlertes.length === 0 ? (

              <div className="table-message">
                Aucune alerte pour le moment.
                Tout est en ordre ! 🎉
              </div>

            ) : (

              <div className="alertes-list">

                {safeAlertes.map(
                  (alerte, index) => {

                    /*
                     * Protection supplémentaire
                     * contre une entrée null/undefined
                     */
                    if (!alerte) {
                      return null;
                    }

                    const alerteId =
                      alerte.id ??
                      `alerte-${index}`;

                    const estLue =
                      Boolean(
                        alerte.est_lue
                      );

                    return (
                      <div
                        key={alerteId}
                        className={`alerte-card ${
                          estLue
                            ? 'lue'
                            : 'non-lue'
                        }`}
                      >

                        {/* GAUCHE */}

                        <div className="alerte-card-left">

                          <PrioriteBadge
                            priorite={
                              alerte.priorite
                            }
                          />

                          <div className="alerte-card-content">

                            <div className="alerte-card-header-row">

                              <span className="alerte-card-titre">
                                {alerte.titre ||
                                  'Alerte'}
                              </span>

                              {!estLue && (
                                <span
                                  className="alerte-nonlue-dot"
                                  title="Non lue"
                                />
                              )}

                            </div>

                            <p className="alerte-card-message">
                              {alerte.message ||
                                'Aucun détail disponible.'}
                            </p>

                            <div className="alerte-card-meta">

                              <span className="alerte-type-badge">

                                {TYPE_LABELS[
                                  alerte.type
                                ] ||
                                  alerte.type ||
                                  'Alerte RH'}

                              </span>

                              {alerte.entite ===
                                'CONTRAT' && (

                                <span className="alerte-entite-tag">

                                  <FileText
                                    size={11}
                                  />

                                  Mon Contrat

                                </span>
                              )}

                              <span className="alerte-date-tag">

                                {formatDateHeure(
                                  alerte.date_creation
                                )}

                              </span>

                            </div>

                          </div>

                        </div>

                        {/* ACTIONS */}

                        <div className="alerte-card-actions">

                          {alerte.entite ===
                            'CONTRAT' && (

                            <button
                              className="alerte-action-btn primary"
                              onClick={() =>
                                handleVoirContrat(
                                  alerte
                                )
                              }
                            >

                              Voir mon contrat

                              <ArrowRight
                                size={13}
                              />

                            </button>
                          )}

                          {!estLue && (
                            <button
                              className="alerte-action-btn secondary"
                              onClick={() =>
                                handleMarkRead(
                                  alerte.id
                                )
                              }
                            >

                              <Check
                                size={13}
                              />

                              Marquer comme lue

                            </button>
                          )}

                        </div>

                      </div>
                    );
                  }
                )}

              </div>
            )}

          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          <div className="pagination-bar-premium">

            <span className="pagination-info-premium">

              {totalGlobal} alerte(s)
              {' - '}
              page {page} / {Math.max(
                1,
                totalPages
              )}

            </span>

            <div className="pagination-controls-premium">

              {/* PREVIOUS */}

              <button
                disabled={page <= 1}
                onClick={() =>
                  setPage(
                    (current) =>
                      Math.max(
                        1,
                        current - 1
                      )
                  )
                }
                className="pagination-circle-btn arrow-btn"
              >
                <ChevronLeft size={16} />
              </button>

              {/* NUMBERS */}

              <div className="pagination-numbers-container">

                {renderPageNumbers()}

              </div>

              {/* NEXT */}

              <button
                disabled={
                  page >=
                  Math.max(
                    1,
                    totalPages
                  )
                }
                onClick={() =>
                  setPage(
                    (current) =>
                      Math.min(
                        Math.max(
                          1,
                          totalPages
                        ),
                        current + 1
                      )
                  )
                }
                className="pagination-circle-btn arrow-btn"
              >
                <ChevronRight size={16} />
              </button>

            </div>

          </div>

        </div>

      </main>

    </div>
  );
}